import moment from 'moment';
import type { ChartOption } from '@/pages/dashboard/components/Chart/_config/interface';
import { getEchartTooltipString } from '@/pages/dashboard/components/EchartsTooltip';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import {
  getTrendUnitFromPeriod,
  normalizeTechlandTrendListDateKey,
  resolveTechlandSevenTickWindow,
  type TTrendDateUnit,
} from '@home/pages/studio/home/_trendUtils';
import type { IMetricSeriesConfig } from './types';

/** 末点标签「占年度计划」英文模板，`{{pct}}` 为整数百分比 */
export const DEFAULT_PLAN_PROGRESS_LABEL_TEMPLATE_EN =
  ' ({{pct}}% of Yearly Plan)';
/** 中文模板 */
export const DEFAULT_PLAN_PROGRESS_LABEL_TEMPLATE_CN =
  '（{{pct}}% 年度计划）';

const DEFAULT_PLAN_PROGRESS_FOR_METRIC_KEYS = [
  'ytd_profit',
  'ytd_gross_revenue',
];

function isEmptyValue(v: unknown): boolean {
  return v === '' || v === null || v === undefined;
}

/** 与 normalize 类目键一致，用于时间序比较 */
function trendCategoryKeyToMoment(key: string, unit: TTrendDateUnit): moment.Moment {
  const s = String(key ?? '').trim();
  if (unit === 'month') {
    const mth = /^(\d{4})-(\d{2})$/.exec(s);
    if (mth) {
      return moment(`${mth[1]}-${mth[2]}-01`, 'YYYY-MM-DD').startOf('month');
    }
  }
  const d = moment(s, 'YYYY-MM-DD', true);
  if (!d.isValid()) {
    return moment(NaN);
  }
  return unit === 'week' ? d.clone().startOf('isoWeek') : d.clone().startOf('day');
}

/**
 * 周类目键为 ISO 周起始周一 YYYY-MM-DD。
 * 若该 ISO 周跨越公历元旦（含「周一所在年的次年 1 月 1 日」落在该周内），则 x 轴 / 浮窗用次年 1 月 1 日展示，避免显示上一年周一（如 12-29）。
 */
function resolveWeekCategoryDisplayMoment(weekCategoryKeyYmd: string): moment.Moment {
  const monday = moment(weekCategoryKeyYmd, 'YYYY-MM-DD', true).startOf('isoWeek');
  if (!monday.isValid()) {
    return moment(weekCategoryKeyYmd, 'YYYY-MM-DD', true);
  }
  const y = monday.year();
  const jan1NextCalYear = moment(`${y + 1}-01-01`, 'YYYY-MM-DD', true);
  const weekEnd = monday.clone().endOf('isoWeek');
  if (
    jan1NextCalYear.isValid() &&
    jan1NextCalYear.isSameOrAfter(monday, 'day') &&
    jan1NextCalYear.isSameOrBefore(weekEnd, 'day')
  ) {
    return jan1NextCalYear.clone().startOf('day');
  }
  return monday.clone().startOf('day');
}

function isListPeriodAtOrBeforeTick(
  listKey: string,
  tickKey: string,
  unit: TTrendDateUnit,
): boolean {
  const lm = trendCategoryKeyToMoment(listKey, unit);
  const tm = trendCategoryKeyToMoment(tickKey, unit);
  if (!lm.isValid() || !tm.isValid()) {
    return false;
  }
  if (unit === 'month') {
    return lm.isSameOrBefore(tm, 'month');
  }
  return lm.isSameOrBefore(tm, 'day');
}

/**
 * 在保留 7 个主刻度类目的前提下，并入 [首刻度, 末刻度] 区间内接口所有实际日期键，
 * 使折线按日/周/月逐点绘制，避免仅 7 点时中间全 null 把有数据段「藏」掉。
 */
function mergeSevenTickAxisWithListDensity(
  sevenKeys: string[],
  sortedListKeys: string[],
  unit: TTrendDateUnit,
): string[] {
  if (!sevenKeys.length) {
    return [];
  }
  const minK = sevenKeys[0];
  const maxK = sevenKeys[sevenKeys.length - 1];
  const merged = new Set<string>(sevenKeys);
  for (const lk of sortedListKeys) {
    if (isListPeriodAtOrBeforeTick(minK, lk, unit) && isListPeriodAtOrBeforeTick(lk, maxK, unit)) {
      merged.add(lk);
    }
  }
  return [...merged].sort(
    (a, b) =>
      trendCategoryKeyToMoment(a, unit).valueOf() -
      trendCategoryKeyToMoment(b, unit).valueOf(),
  );
}

/** 升序列表键中，时间不大于 tick 的最后一条（用于插值刻度对齐到最近一条接口数据） */
function findLatestListKeyAtOrBeforeTick(
  tickKey: string,
  unit: TTrendDateUnit,
  sortedAscListKeys: string[],
): string | null {
  let best: string | null = null;
  for (const k of sortedAscListKeys) {
    if (isListPeriodAtOrBeforeTick(k, tickKey, unit)) {
      best = k;
    }
  }
  return best;
}

/**
 * 7 刻度横轴类目常为时间插值，与接口自然日键不完全一致：
 * 取「接口中 ≤ 该刻度的最后一条」该指标原始值；无键或值为空则 null，不向前填充，折线断开。
 * 刻度晚于接口最后一条数据时返回 null。
 */
function resolveSevenTickMetricValueAtTick(
  tickKey: string,
  unit: TTrendDateUnit,
  sortedAscListKeys: string[],
  getValue: (listKey: string) => unknown,
): unknown {
  if (!sortedAscListKeys.length) {
    return null;
  }
  const lastListKey = sortedAscListKeys[sortedAscListKeys.length - 1];
  const tickM = trendCategoryKeyToMoment(tickKey, unit);
  const lastM = trendCategoryKeyToMoment(lastListKey, unit);
  if (!tickM.isValid() || !lastM.isValid()) {
    return null;
  }
  const afterAllData =
    unit === 'month' ? tickM.isAfter(lastM, 'month') : tickM.isAfter(lastM, 'day');
  if (afterAllData) {
    return null;
  }
  const latestKey = findLatestListKeyAtOrBeforeTick(tickKey, unit, sortedAscListKeys);
  if (!latestKey) {
    return null;
  }
  const v = getValue(latestKey);
  return isEmptyValue(v) ? null : v;
}

function getLastNonEmptyIndex(arr: unknown[]): number {
  return arr.reduce<number>((last, v, i) => (!isEmptyValue(v) ? i : last), -1);
}

function unwrapSeriesCellValue(cell: unknown): unknown {
  if (typeof cell === 'object' && cell !== null && 'value' in cell) {
    return (cell as { value?: unknown; }).value;
  }
  return cell;
}

function isEmptySeriesCell(cell: unknown): boolean {
  return isEmptyValue(unwrapSeriesCellValue(cell));
}

/** 前后均为空、当前有值：展示孤立圆点（与折线 symbol 尺寸一致，不改变线色与线宽） */
function withIsolatedValueDots(
  data: unknown[],
  lineColor: string | undefined,
  skipIndex: number,
): unknown[] {
  const dotItemStyle = lineColor?.trim()
    ? { opacity: 1 as const, color: lineColor.trim() }
    : { opacity: 1 as const };
  return data.map((cell, i) => {
    if (i === skipIndex || isEmptySeriesCell(cell)) {
      return cell;
    }
    const prevEmpty = i === 0 || isEmptySeriesCell(data[i - 1]);
    const nextEmpty = i === data.length - 1 || isEmptySeriesCell(data[i + 1]);
    if (!prevEmpty || !nextEmpty) {
      return cell;
    }
    const raw = unwrapSeriesCellValue(cell);
    return {
      value: raw,
      symbol: 'circle',
      symbolSize: 8,
      itemStyle: dotItemStyle,
    };
  });
}

function formatMetricValue(value: unknown, formatter: string | undefined): string {
  if (isEmptyValue(value)) {
    return '';
  }
  return String(
    dataFormat({
      value: value as string | number,
      formatRule: formatter || 'bmk00',
    }),
  );
}

/** 图例 / tooltip 展示名：英文用 enName，中文优先 cnName */
export function getSeriesDisplayName(
  m: IMetricSeriesConfig,
  localeIsEn: boolean,
): string {
  if (localeIsEn) {
    return m.enName;
  }
  const cn = m.cnName?.trim();
  return cn || m.enName;
}

export interface IMultiMetricChartParams {
  metrics: IMetricSeriesConfig[];
  yAxisFormatter?: string;
  xDateFormat?: string;
  tooltipDateFormat?: string;
  dateType?: string;
  dateFormat?: string;
  planValueForPercent?: number | string;
  labelTextStyle?: { fontSize?: number; color?: string; };
  targetLines?: Array<{
    label: string;
    value: number;
    color?: string;
    labelFormatter?: string;
    /** 是否在 tooltip 浮窗中展示该参考线（默认展示；由上层注入可控制） */
    showInTooltip?: boolean;
  }>;
  /**
   * 折线末点「占年度计划」后缀，`{{pct}}` 替换为整数百分比。
   * 不传用英文默认；传空字符串则不展示该后缀。
   */
  planProgressLabelTemplate?: string;
  /** 参与计划占比的主收入序列 metricKey，默认 ytd_profit / ytd_gross_revenue */
  planProgressForMetricKeys?: string[];
  /** 与当前语言一致：true 英文图例/tooltip，false 中文（用 series 的 cnName） */
  localeIsEn?: boolean;
  /**
   * sevenTick2026Mode 注入：仅影响横轴类目与 list→类目键映射（resolveTechlandSevenTickWindow），
   * 折线/坐标轴样式与改 sevenTick 前一致。
   */
  sevenTickAxis?: {
    filterEndYmd?: string;
    period?: string;
  };
}

function buildTargetLineLabelText(
  rawValue: number,
  labelFmt: string | undefined,
  defaultFmt: string | undefined,
): string {
  return formatMetricValue(rawValue, labelFmt || defaultFmt);
}

export function buildMultiMetricTrendChartOptions(
  list: Array<{ date?: string; metrics?: Record<string, { value?: unknown; }>; }>,
  params: IMultiMetricChartParams,
): ChartOption {
  const {
    metrics,
    yAxisFormatter = 'intBmk',
    xDateFormat = 'MM-DD',
    tooltipDateFormat,
    dateFormat: paramsDateFormat = 'YYYY-MM-DD',
    planValueForPercent,
    labelTextStyle,
    targetLines,
    planProgressLabelTemplate: planProgressTplParam,
    planProgressForMetricKeys,
    localeIsEn = true,
    sevenTickAxis,
  } = params;

  const planProgressKeys =
    Array.isArray(planProgressForMetricKeys) &&
    planProgressForMetricKeys.some(k => k && String(k).trim())
      ? planProgressForMetricKeys.filter(k => k && String(k).trim())
      : DEFAULT_PLAN_PROGRESS_FOR_METRIC_KEYS;

  let planProgressLabelTemplate: string | null;
  if (planProgressTplParam === '') {
    planProgressLabelTemplate = null;
  } else if (
    planProgressTplParam !== undefined &&
    String(planProgressTplParam).trim() !== ''
  ) {
    planProgressLabelTemplate = String(planProgressTplParam);
  } else {
    planProgressLabelTemplate = DEFAULT_PLAN_PROGRESS_LABEL_TEMPLATE_EN;
  }

  if (!metrics.length) {
    return {};
  }

  const ftEnd = String(sevenTickAxis?.filterEndYmd ?? '').trim();
  let xAxisData: string[];
  let listDateUnit: TTrendDateUnit | null = null;
  let sevenTickMajorKeys: string[] = [];

  if (ftEnd && sevenTickAxis) {
    const { xCategoryKeys } = resolveTechlandSevenTickWindow(
      ftEnd,
      sevenTickAxis.period,
    );
    sevenTickMajorKeys = xCategoryKeys;
    listDateUnit = getTrendUnitFromPeriod(sevenTickAxis.period);
  }

  /** 7 刻度模式：接口列表中所有归一化日期键（升序） */
  let sortedListDateKeysForSevenTick: string[] = [];
  if (ftEnd && sevenTickAxis && listDateUnit) {
    const dk = new Set<string>();
    list?.forEach(item => {
      const k = normalizeTechlandTrendListDateKey(item?.date, listDateUnit);
      if (k) {
        dk.add(k);
      }
    });
    sortedListDateKeysForSevenTick = [...dk].sort(
      (a, b) =>
        trendCategoryKeyToMoment(a, listDateUnit).valueOf() -
        trendCategoryKeyToMoment(b, listDateUnit).valueOf(),
    );
  }

  if (ftEnd && sevenTickAxis && listDateUnit && sevenTickMajorKeys.length > 0) {
    if (sortedListDateKeysForSevenTick.length > 0) {
      xAxisData = mergeSevenTickAxisWithListDensity(
        sevenTickMajorKeys,
        sortedListDateKeysForSevenTick,
        listDateUnit,
      );
    } else {
      xAxisData = [...sevenTickMajorKeys];
    }
  } else {
    const xSet = new Set<string>();
    list?.forEach(item => {
      if (item?.date) {
        const k = moment(item.date).format(paramsDateFormat);
        xSet.add(k);
      }
    });
    xAxisData = [...xSet].sort();
  }

  const planVal =
    planValueForPercent !== null &&
    planValueForPercent !== undefined &&
    planValueForPercent !== ''
      ? Number(planValueForPercent)
      : null;

  const series = metrics.map(m => {
    const dateValueMap: Record<string, unknown> = {};
    list?.forEach(item => {
      const k = listDateUnit
        ? normalizeTechlandTrendListDateKey(item?.date, listDateUnit)
        : moment(item?.date).format(paramsDateFormat);
      if (!k) {
        return;
      }
      const raw = item?.metrics?.[m.metricKey]?.value;
      dateValueMap[k] = isEmptyValue(raw) ? null : raw;
    });
    const useSevenTickDenseAxis =
      ftEnd &&
      sevenTickAxis &&
      listDateUnit &&
      sortedListDateKeysForSevenTick.length > 0;

    let seriesData: unknown[];
    if (useSevenTickDenseAxis) {
      seriesData = xAxisData.map(k => dateValueMap[k] ?? null);
    } else if (ftEnd && sevenTickAxis && listDateUnit) {
      seriesData = xAxisData.map(tickKey =>
        resolveSevenTickMetricValueAtTick(
          tickKey,
          listDateUnit,
          sortedListDateKeysForSevenTick,
          lk => dateValueMap[lk] ?? null,
        ),
      );
    } else {
      seriesData = xAxisData.map(d => dateValueMap[d] ?? null);
    }
    const lastIndex = getLastNonEmptyIndex(seriesData);
    const fmt = m.labelFormatter || m.formatter || yAxisFormatter;
    const lineColor = m.lineColor?.trim();
    const colorStyle = lineColor ? { color: lineColor } : {};
    const skipIsolatedAt = m.showLabel && lastIndex >= 0 ? lastIndex : -1;
    seriesData = withIsolatedValueDots(seriesData, lineColor, skipIsolatedAt);

    if (m.showLabel && lastIndex >= 0) {
      const v = unwrapSeriesCellValue(seriesData[lastIndex]);
      seriesData[lastIndex] = {
        value: v,
        symbol: 'circle',
        itemStyle: { opacity: 1, ...colorStyle },
        label: {
          show: true,
          position: 'right',
          offset: [6, 0],
          ...(labelTextStyle?.fontSize ? { fontSize: labelTextStyle.fontSize } : {}),
          ...(labelTextStyle?.color ? { color: labelTextStyle.color } : {}),
          formatter: (p: { dataIndex?: number; value?: unknown; }) => {
            if (p.dataIndex !== lastIndex) {
              return '';
            }
            let val: unknown;
            if (
              typeof p.value === 'object' &&
              p.value !== null &&
              'value' in p.value
            ) {
              val = (p.value as { value?: unknown; }).value;
            } else {
              val = p.value;
            }
            let text = formatMetricValue(val, fmt);
            if (
              planProgressLabelTemplate &&
              planVal !== null &&
              planVal > 0 &&
              !isEmptyValue(val) &&
              planProgressKeys.includes(m.metricKey)
            ) {
              const num = Number(val);
              if (Number.isFinite(num)) {
                const pct = Math.round((num / planVal) * 100);
                text += planProgressLabelTemplate.replace(
                  /\{\{pct\}\}/g,
                  String(pct),
                );
              }
            }
            return text;
          },
        },
      };
    }
    return {
      name: getSeriesDisplayName(m, localeIsEn),
      type: 'line' as const,
      ...(lineColor ? { color: lineColor } : {}),
      data: seriesData,
      yAxisIndex: 0,
      legendHoverLink: false,
      connectNulls: false,
      symbol: 'circle',
      symbolSize: 8,
      lineStyle: {
        type: m.lineType === 'dashed' ? 'dashed' : 'solid',
        width: 1.8,
        ...colorStyle,
      },
      itemStyle: { opacity: 0, ...colorStyle },
      emphasis: {
        focus: 'none',
        itemStyle: { borderColor: '#fff', borderWidth: 1, borderType: 'solid', opacity: 1 },
      },
    };
  });

  const ttFmt = tooltipDateFormat || paramsDateFormat;

  const sevenTickMajorKeySet = new Set(
    sevenTickMajorKeys
      .map(k =>
        listDateUnit ? normalizeTechlandTrendListDateKey(String(k), listDateUnit) : String(k),
      )
      .filter(Boolean),
  );

  /** 类目轴 interval 回调签名为 (tickValue, rawLabel)，见 echarts axisTickLabelBuilder */
  const isSevenTickMajorCategory = (tickVal: number, rawLabel: string): boolean => {
    const raw = String(rawLabel ?? xAxisData[tickVal] ?? '');
    const key = listDateUnit ? normalizeTechlandTrendListDateKey(raw, listDateUnit) : raw;
    return sevenTickMajorKeySet.has(key);
  };

  const option: ChartOption = {
    tooltip: {
      trigger: 'axis',
      formatter: (items: unknown) => {
        const arr = Array.isArray(items) ? items : [];
        if (!arr.length) {
          return '';
        }
        const first = arr[0] as { axisValue?: string; };
        const axis = first.axisValue ?? '';
        const axisKey =
          listDateUnit && axis
            ? normalizeTechlandTrendListDateKey(String(axis), listDateUnit)
            : String(axis);
        let dateTitle = String(axis);
        if (listDateUnit === 'week' && axisKey) {
          const dm = resolveWeekCategoryDisplayMoment(axisKey);
          dateTitle = dm.isValid() ? dm.format(ttFmt) : String(axis);
        } else if (moment(axis).isValid()) {
          dateTitle = moment(axis).format(ttFmt);
        }

        const data = arr
          .map((it: any) => {
            let raw: unknown = it.value;
            if (typeof raw === 'object' && raw !== null && 'value' in raw) {
              raw = (raw as { value?: unknown; }).value;
            }
            const ser = metrics.find(
              s => getSeriesDisplayName(s, localeIsEn) === it.seriesName,
            );
            const tl = targetLines?.find(t => t.label === it.seriesName);
            if (tl && tl.showInTooltip === false) {
              return null;
            }
            const fr =
              ser?.tooltipFormatter ||
              ser?.formatter ||
              tl?.labelFormatter ||
              yAxisFormatter;
            const text = isEmptyValue(raw) ? '-' : formatMetricValue(raw, fr);
            return {
              name: String(it.seriesName ?? ''),
              color: it.color ?? '',
              value: text,
            };
          })
          .filter(Boolean);

        return getEchartTooltipString({
          table: {
            title: dateTitle,
            hideHeader: true,
            columns: [
              {
                key: 'name',
                header: '',
                showIcon: true,
                iconType: 'circular',
              },
              {
                key: 'value',
                header: '',
                align: 'right',
              },
            ],
            data,
          },
        });
      },
      padding: 0
    },
    legend: {
      data: metrics.map(m => {
        const c = m.lineColor?.trim();
        return {
          name: getSeriesDisplayName(m, localeIsEn),
          icon: 'circle',
          itemStyle: {
            opacity: 1,
            ...(c ? { color: c } : {}),
          },
        };
      }),
      bottom: 0,
      itemWidth: 8,
      itemHeight: 8,
      itemGap: 16,
    },
    grid: {
      /** 7 刻度 + 密集类目时末位标签居中会向右探出，需留白避免裁切 */
      right: sevenTickMajorKeySet.size > 0 ? 36 : 12,
      left: 2,
      top: 24,
      bottom: 35,
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        data: xAxisData,
        axisLabel: {
          color: '#838697',
          fontSize: 10,
          align: 'center',
          ...(sevenTickMajorKeySet.size > 0
            ? { interval: 0, hideOverlap: false }
            : {}),
          formatter: (v: string) => {
            const key = listDateUnit
              ? normalizeTechlandTrendListDateKey(String(v), listDateUnit)
              : String(v);
            if (sevenTickMajorKeySet.size > 0 && !sevenTickMajorKeySet.has(key)) {
              return '';
            }
            const labelSrc = key || String(v);
            if (listDateUnit === 'week' && labelSrc) {
              const dm = resolveWeekCategoryDisplayMoment(labelSrc);
              return dm.isValid() ? dm.format(xDateFormat) : labelSrc;
            }
            return moment(labelSrc).isValid() ? moment(labelSrc).format(xDateFormat) : labelSrc;
          },
        },
        axisTick: {
          show: true,
          alignWithLabel: true,
          ...(sevenTickMajorKeySet.size > 0
            ? { interval: (tickVal: number, rawLabel: string) => isSevenTickMajorCategory(tickVal, rawLabel) }
            : {}),
        },
        axisLine: {
          lineStyle: {
            color: '#D8DAE4',
          },
          show: true
        },
        splitLine: { show: false },
      },
    ],
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          formatter: (value: number) => formatMetricValue(value, yAxisFormatter),
          color: '#838697',
          fontSize: 12
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#ddd',
          },
        },
      },
    ],
    series: series as any,
  };

  if (targetLines?.length && xAxisData.length) {
    const xLen = xAxisData.length;
    targetLines.forEach((tl, idx) => {
      const color = tl.color?.trim() || '#888';
      const labelText = buildTargetLineLabelText(
        tl.value,
        tl.labelFormatter,
        yAxisFormatter,
      );
      const labelWithPrefix = tl.label ? `${tl.label} ${labelText}` : labelText;
      (option.series as any[]).push({
        name: tl.label,
        type: 'line',
        color,
        legendHoverLink: false,
        data: new Array(xLen).fill(tl.value),
        lineStyle: { type: 'dashed', color, width: 1.8 },
        symbol: 'circle',
        symbolSize: 8,
        itemStyle: { opacity: 0, color },
        emphasis: {
          focus: 'none',
          itemStyle: { borderColor: '#fff', borderWidth: 1, borderType: 'solid', opacity: 1 },
        },
        markPoint: {
          animation: false,
          symbol: 'circle',
          symbolSize: 1,
          data: [
            {
              coord: [xAxisData[0], tl.value],
              label: {
                show: true,
                position: idx === 0 ? 'top' : 'bottom',
                formatter: () => labelWithPrefix,
                color,
                fontSize: 12,
                fontWeight: 'normal',
                align: 'left',
                offset: [0, idx === 0 ? -1 : 1],
              },
              emphasis: {
                disabled: true,
              },
            },
          ],
        },
      });
    });
    const leg = option.legend as {
      data?: Array<string | { name: string; icon?: string; itemStyle?: Record<string, unknown>; }>;
    };
    if (leg?.data) {
      targetLines.forEach(tl => {
        const color = tl.color?.trim() || '#888';
        leg.data!.push({
          name: tl.label,
          icon: 'circle',
          itemStyle: { opacity: 1, color },
        });
      });
    }
  }

  return option;
}
